import { useCallback, useRef, useState } from 'react';
import { mutate } from 'swr';
import { useAppInsights } from '../../../components/AppInsightsWrapper/AppInsightsWrapper';
import { useSubscriptionKey } from '../../../components/subscriptionKey/useSubscriptionKey';
import { useGetToken } from '../../../contexts/TokenContext/useToken';
import { authenticatedFetch } from './authenticatedFetch';
import { useAuthenticatedUrl } from './useAuthenticatedUrl';

/**
 * Common hook for any types of updates
 *
 * @param url
 * @returns
 */
export function useAuthenticatedJsonUpdate<ResponseDataType>(url: string | undefined) {
	const appInsights = useAppInsights();

	const [pending, setPending] = useState<boolean>(false);

	const currentAction = useRef<string | undefined>(undefined);
	const currentPromise = useRef<Promise<ResponseDataType | undefined> | undefined>(undefined);

	const [subscriptionKey] = useSubscriptionKey();
	// Don't fetch until we have a subscription key
	const validURL = useAuthenticatedUrl(url);
	const getToken = useGetToken();

	const updater = useCallback(
		async (data?: unknown, action = 'POST') => {
			// Don't do anything without a valid URL
			if (!validURL) {
				return undefined;
			}

			// Don't allow double submit
			if (!pending) {
				setPending(true);
				currentAction.current = action;
				currentPromise.current = authenticatedFetch<ResponseDataType>(
					appInsights,
					subscriptionKey,
					getToken,
					validURL,
					action,
					data,
				);
			} else {
				if (currentAction.current !== action) {
					// Different action is already in progress, do nothing
					return undefined;
				}
			}

			// Submit data
			const result = await currentPromise.current;

			// Trigger a mutation
			await mutate(validURL);

			currentAction.current = undefined;
			currentPromise.current = undefined;
			setPending(false);

			// Return the result
			return result;
		},
		[appInsights, subscriptionKey, getToken, validURL, pending],
	);

	return { updater, pending };
}
